import type { ReportId, PlaceId, EventId } from "./ids";
import type { SourceLink } from "./source";

/**
 * 실측 정정 (2026-09-08, 로컬 백엔드 대조).
 * `report_service.REPORT_STATUSES` 는 **9개**다 — 초기 문서가 `NEEDS_MORE_INFO` 를 빠뜨렸다.
 */
export const REPORT_STATUSES = [
  "DRAFT", "SUBMITTED", "UNDER_REVIEW", "NEEDS_MORE_INFO", "APPROVED",
  "PARTIALLY_APPROVED", "REJECTED", "ON_HOLD", "WITHDRAWN",
] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

/** `report_service.REPORT_TYPES` 실측 16종. */
export const REPORT_TYPES = [
  "NEW_PLACE", "NEW_EVENT", "PLACE_CHANGE", "EVENT_CHANGE",
  "PLACE_EDIT", "EVENT_EDIT", "PLACE_CLOSED", "EVENT_ENDED",
  "DUPLICATE_PLACE", "DUPLICATE_EVENT", "WRONG_LOCATION", "WRONG_INFORMATION",
  "LIVE_UPDATE", "CLOSURE", "COPYRIGHT", "OTHER",
] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

/** targetPlaceId 가 반드시 있어야 하는 유형 (routes.py 실측). */
export const REPORT_TYPES_REQUIRING_PLACE: readonly ReportType[] = ["PLACE_CHANGE", "CLOSURE"];

export const REPORT_DECISIONS = ["PENDING", "APPROVED", "REJECTED"] as const;
export type ReportDecision = (typeof REPORT_DECISIONS)[number];

export interface ReportChange {
  id: string;
  fieldPath: string;
  oldValue?: unknown;
  proposedValue?: unknown;
  decision: ReportDecision | string;
  decisionNote?: string | null;
}

export interface Report {
  id: ReportId;
  reportType: string;
  title: string;
  summary?: string | null;
  status: ReportStatus;
  reviewNote?: string | null;
  createdAt: string;
  submittedAt?: string | null;
  targetPlaceId?: PlaceId | null;
  targetEventId?: EventId | null;
  changes: ReportChange[];
  sourceLinks: SourceLink[];
}

/**
 * `report_service.TRANSITIONS` 를 그대로 옮긴 것이다.
 * ⚠️ 초기 문서는 `REJECTED → DRAFT` 를 적었으나 **서버에는 그런 전이가 없다.**
 * 반려는 종결이며, 다시 내려면 새 제보를 만든다.
 */
const TRANSITIONS: Readonly<Record<ReportStatus, readonly ReportStatus[]>> = {
  DRAFT: ["SUBMITTED"],
  SUBMITTED: ["UNDER_REVIEW", "WITHDRAWN"],
  UNDER_REVIEW: ["APPROVED", "PARTIALLY_APPROVED", "REJECTED", "NEEDS_MORE_INFO", "ON_HOLD", "SUBMITTED"],
  NEEDS_MORE_INFO: ["SUBMITTED", "WITHDRAWN", "ON_HOLD"],
  ON_HOLD: ["UNDER_REVIEW", "WITHDRAWN"],
  APPROVED: [],
  PARTIALLY_APPROVED: [],
  REJECTED: [],
  WITHDRAWN: [],
};

export function canTransition(from: ReportStatus, to: ReportStatus): boolean {
  return TRANSITIONS[from].includes(to);
}
export function isTerminal(s: ReportStatus): boolean {
  return TRANSITIONS[s].length === 0;
}
/**
 * 사용자가 이어서 손댈 수 있는 상태인가. 반려는 종결이므로 새 제보를 만들어야 한다.
 *
 * ⚠️ **아직 그 길이 없다.** `reports` 에 수정 엔드포인트가 없어서, 이 함수가
 * `true` 를 돌려줘도 실제로 고칠 방법이 없다. 규칙은 맞고 구현이 없는
 * 것이므로 지우지 않는다 — 다만 화면 문구가 "출처를 더 알려주세요" 라고
 * 말하고 있었고, 그것은 할 수 없는 일을 시키는 것이었다 (2026-09-10 수정).
 */
export function isEditable(s: ReportStatus): boolean {
  return s === "DRAFT" || s === "NEEDS_MORE_INFO";
}
/** P6: 승인 전에는 원본을 덮어쓰지 않는다. */
export function affectsPublicData(s: ReportStatus): boolean {
  return s === "APPROVED" || s === "PARTIALLY_APPROVED";
}
